"use client";

import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, UploadCloud } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button, ListSurface, ListSurfaceHeader, WorkspacePage } from "@/components/ds";
import { Card } from "@/components/ui-system";
import { ThemedSelect } from "@/components/ui-system/select";
import {
  createOtaCampaign,
  getOtaCampaign,
  listOtaTargetDevices,
  updateOtaCampaign,
} from "@/lib/api/endpoints/ota";
import { listUsers } from "@/lib/api/endpoints/users";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";

type TargetMode = "devices" | "admins";
const ALLOWED_FIRMWARE_EXTENSIONS = [".bin", ".hex"] as const;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
];

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export function OtaFormPage({ campaignId }: { campaignId?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const editing = Boolean(campaignId);
  const [targetMode, setTargetMode] = useState<TargetMode>("devices");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [file, setFile] = useState<File | null>(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const campaignQuery = useQuery({
    queryKey: ["ota", "campaign", campaignId],
    queryFn: () => getOtaCampaign(campaignId!),
    enabled: editing && Boolean(campaignId),
    staleTime: 20_000,
  });

  const devicesQuery = useQuery({
    queryKey: ["ota", "target-devices"],
    queryFn: listOtaTargetDevices,
    enabled: !editing,
    staleTime: 30_000,
  });

  const adminsQuery = useQuery({
    queryKey: ["ota", "admins"],
    queryFn: () => listUsers(null, "all"),
    enabled: !editing && superAdmin,
    staleTime: 60_000,
  });

  const admins = useMemo(
    () => (adminsQuery.data?.items ?? []).filter((item) => item.role === "admin"),
    [adminsQuery.data?.items],
  );
  const devices = devicesQuery.data ?? [];
  const allDeviceIds = useMemo(() => devices.map((device) => device.id), [devices]);
  const allAdminIds = useMemo(() => admins.map((admin) => admin.id), [admins]);
  const allDevicesSelected = allDeviceIds.length > 0 && allDeviceIds.every((id) => selectedDeviceIds.includes(id));
  const allAdminsSelected = allAdminIds.length > 0 && allAdminIds.every((id) => selectedAdminIds.includes(id));

  useEffect(() => {
    const campaign = campaignQuery.data;
    if (!campaign) {
      return;
    }
    setVersion(campaign.version);
    setDescription(campaign.description ?? "");
    setStatus(campaign.status || "active");
  }, [campaignQuery.data]);

  useEffect(() => {
    const deviceId = searchParams.get("device");
    if (editing || !deviceId || devicesQuery.isLoading) {
      return;
    }
    if ((devicesQuery.data ?? []).some((device) => device.id === deviceId)) {
      setTargetMode("devices");
      setSelectedDeviceIds((current) => (current.includes(deviceId) ? current : [...current, deviceId]));
    }
  }, [devicesQuery.data, devicesQuery.isLoading, editing, searchParams]);

  const createMutation = useMutation({
    mutationFn: createOtaCampaign,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ota"] });
      navigate("/ota");
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateOtaCampaign(campaignId!, { description, status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ota"] });
      navigate("/ota");
    },
  });

  const selectedTargetCount = targetMode === "admins" ? selectedAdminIds.length : selectedDeviceIds.length;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (editing) {
      updateMutation.mutate();
      return;
    }
    if (!file) {
      setError("Firmware .bin or .hex file is required.");
      return;
    }
    const lowerName = file.name.toLowerCase();
    if (!ALLOWED_FIRMWARE_EXTENSIONS.some((extension) => lowerName.endsWith(extension))) {
      setError("Only .bin and .hex firmware files are allowed.");
      return;
    }
    if (!version.trim()) {
      setError("Version is required.");
      return;
    }
    if (selectedTargetCount === 0) {
      setError(targetMode === "admins" ? "Select at least one admin." : "Select at least one device.");
      return;
    }
    createMutation.mutate({
      file,
      version: version.trim(),
      description,
      deviceIds: targetMode === "devices" ? selectedDeviceIds : [],
      adminUserIds: targetMode === "admins" ? selectedAdminIds : [],
    });
  };

  const pending = createMutation.isPending || updateMutation.isPending;
  const submitError = createMutation.error ?? updateMutation.error;

  return (
    <WorkspacePage className="max-w-[74rem]">
      <div className="space-y-ds-5">
        <Link to="/ota" className="btn btn-secondary btn-sm w-fit !no-underline">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to OTA
        </Link>

        <ListSurface>
          <ListSurfaceHeader>
            <div className="flex items-center gap-ds-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-control bg-primary/10 text-primary">
                <UploadCloud className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-foreground">{editing ? "Edit OTA campaign" : "Create OTA campaign"}</h2>
                <p className="mt-1 text-xs text-muted">
                  {editing ? "Update campaign status and description." : "Upload firmware and select rollout targets."}
                </p>
              </div>
            </div>
          </ListSurfaceHeader>

          <form className="grid gap-ds-5 p-ds-6" onSubmit={onSubmit}>
            {editing && campaignQuery.isLoading ? (
              <p className="text-caption text-muted">Loading campaign...</p>
            ) : null}

            <div className="grid gap-ds-4 md:grid-cols-2">
              <label className="block space-y-ds-2">
                <span className="text-micro font-semibold uppercase tracking-wider text-muted">Version</span>
                <input
                  value={version}
                  onChange={(event) => setVersion(event.target.value)}
                  disabled={editing}
                  className="ui-control"
                  placeholder="1.0.0"
                  required={!editing}
                />
              </label>
              {editing ? (
                <label className="block space-y-ds-2">
                  <span className="text-micro font-semibold uppercase tracking-wider text-muted">Status</span>
                  <ThemedSelect value={status} ariaLabel="Campaign status" options={STATUS_OPTIONS} onChange={setStatus} />
                </label>
              ) : (
                <label className="block space-y-ds-2">
                  <span className="text-micro font-semibold uppercase tracking-wider text-muted">Firmware file</span>
                  <input
                    type="file"
                    accept=".bin,.hex,application/octet-stream,text/plain"
                className="ui-control block w-full rounded-control bg-surface-primary px-ds-3 py-ds-2 text-body"
                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                    required
                  />
                </label>
              )}
            </div>

            <label className="block space-y-ds-2">
              <span className="text-micro font-semibold uppercase tracking-wider text-muted">Description</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="ui-control min-h-28 w-full rounded-control bg-surface-primary px-ds-3 py-ds-2 text-body"
                placeholder="Optional release note"
              />
            </label>

            {!editing ? (
              <Card className="border-border/60 p-ds-4 shadow-none">
                <div className="mb-ds-3 flex flex-wrap items-center justify-between gap-ds-3">
                  <span className="text-micro font-semibold uppercase tracking-wider text-muted">Targets</span>
                  {superAdmin ? (
                    <div className="inline-flex rounded-control border border-card-border bg-surface-secondary p-1">
                      <button
                        type="button"
                        className={`rounded-control px-ds-3 py-ds-1 text-caption transition-colors ${targetMode === "devices" ? "bg-accent text-accent-foreground shadow-raised" : "text-muted hover:text-foreground"}`}
                        onClick={() => setTargetMode("devices")}
                      >
                        Devices
                      </button>
                      <button
                        type="button"
                        className={`rounded-control px-ds-3 py-ds-1 text-caption transition-colors ${targetMode === "admins" ? "bg-accent text-accent-foreground shadow-raised" : "text-muted hover:text-foreground"}`}
                        onClick={() => setTargetMode("admins")}
                      >
                        Admins
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="max-h-80 overflow-auto rounded-card border border-card-border">
                  {targetMode === "admins" ? (
                    admins.length === 0 ? (
                      <p className="p-ds-4 text-caption text-muted">No admins available.</p>
                    ) : (
                      <>
                        <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-ds-3 border-b border-card-border bg-surface-secondary px-ds-4 py-ds-3">
                          <input
                            type="checkbox"
                            checked={allAdminsSelected}
                            onChange={() => setSelectedAdminIds(allAdminsSelected ? [] : allAdminIds)}
                          />
                          <span className="text-body font-semibold text-foreground">Select all admins</span>
                        </label>
                        {admins.map((admin) => (
                        <label key={admin.id} className="flex cursor-pointer items-center gap-ds-3 border-b border-border/50 px-ds-4 py-ds-3 last:border-b-0">
                          <input
                            type="checkbox"
                            checked={selectedAdminIds.includes(admin.id)}
                            onChange={() => setSelectedAdminIds((current) => toggleValue(current, admin.id))}
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-body font-medium text-foreground">{admin.name || admin.email}</span>
                            <span className="block truncate text-caption text-muted">{admin.email}</span>
                          </span>
                        </label>
                        ))}
                      </>
                    )
                  ) : devicesQuery.isLoading ? (
                    <p className="p-ds-4 text-caption text-muted">Loading devices...</p>
                  ) : devices.length === 0 ? (
                    <p className="p-ds-4 text-caption text-muted">No devices available for OTA.</p>
                  ) : (
                    <>
                      <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-ds-3 border-b border-card-border bg-surface-secondary px-ds-4 py-ds-3">
                        <input
                          type="checkbox"
                          checked={allDevicesSelected}
                          onChange={() => setSelectedDeviceIds(allDevicesSelected ? [] : allDeviceIds)}
                        />
                        <span className="text-body font-semibold text-foreground">Select all devices</span>
                      </label>
                      {devices.map((device) => (
                      <label key={device.id} className="flex cursor-pointer items-center gap-ds-3 border-b border-border/50 px-ds-4 py-ds-3 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={selectedDeviceIds.includes(device.id)}
                          onChange={() => setSelectedDeviceIds((current) => toggleValue(current, device.id))}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-body font-medium text-foreground">{device.name || device.device_uid}</span>
                          <span className="block truncate text-caption text-muted">
                            {device.device_uid} · Firmware {device.firmware_version ?? "--"}
                          </span>
                        </span>
                      </label>
                      ))}
                    </>
                  )}
                </div>
              </Card>
            ) : null}

            {error || submitError ? (
              <p className="rounded-control border border-error/25 bg-error/10 px-ds-3 py-ds-2 text-caption text-error" role="alert">
                {error ?? (submitError instanceof Error ? submitError.message : "Could not save OTA campaign.")}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-ds-2">
              <Link to="/ota" className="btn btn-secondary btn-sm !no-underline">
                Cancel
              </Link>
              <Button type="submit" disabled={pending || (editing && campaignQuery.isLoading)}>
                {pending ? "Saving..." : editing ? "Save changes" : "Create campaign"}
              </Button>
            </div>
          </form>
        </ListSurface>
      </div>
    </WorkspacePage>
  );
}
